import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import BrandMark from "../../components/BrandMark";
import { ArrowLeft, LogOut, Lock } from "lucide-react";

/**
 * Shared light-themed layout for all live Gestionale modules.
 * Mirrors the dashboard's ivory background + gold accents.
 */
export default function GestionaleShell({ icon: Icon, code, title, subtitle, kpiBlocks, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  if (!user || !["super_admin", "admin_broker", "buyer"].includes(user.role)) {
    return (
      <div className="min-h-screen bg-[#faf7f0] text-stone-900 flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-4">
          <Lock className="h-10 w-10 text-[#C9A961] mx-auto" />
          <h2 className="editorial-heading text-2xl">Accesso riservato</h2>
          <Link to="/gestionale" className="inline-block px-6 py-3 bg-[#C9A961] text-white font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#A88A4A]">
            Vai al login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`gest-shell-${code}`} className="min-h-screen bg-gradient-to-b from-[#faf7f0] via-[#f7f1e3] to-[#f5efe1] text-stone-900">
      <header className="border-b border-[#C9A961]/30 bg-white/80 backdrop-blur sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <BrandMark size="md" variant="dark" />
          <div className="flex items-center gap-2 md:gap-3">
            <Link to="/gestionale/dashboard" data-testid="back-dashboard" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-stone-600 hover:text-[#C9A961] px-3 py-2">
              <ArrowLeft className="h-3 w-3" /> Dashboard
            </Link>
            <button
              data-testid="topbar-logout"
              onClick={() => { logout(); nav("/gestionale"); }}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] bg-stone-100 hover:bg-red-50 hover:text-red-700 text-stone-700 px-3 py-2 border border-stone-300"
            >
              <LogOut className="h-3 w-3" /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 md:py-14">
        <div className="grid md:grid-cols-[1fr_auto] gap-8 items-end mb-8 pb-8 border-b border-[#C9A961]/30">
          <div>
            <div className="flex items-center gap-3 mb-3">
              {Icon && <Icon className="h-5 w-5 text-[#C9A961]" />}
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#A88A4A] font-bold">Modulo {code} · Live</span>
            </div>
            <h1 className="editorial-heading text-3xl md:text-5xl leading-[1.05] text-stone-900">{title}</h1>
            {subtitle && <p className="text-stone-500 mt-3 text-sm max-w-2xl">{subtitle}</p>}
          </div>
          {kpiBlocks && kpiBlocks.length > 0 && (
            <div className="grid grid-cols-2 gap-6 md:gap-10 text-right">
              {kpiBlocks.slice(0, 2).map((k, i) => (
                <div key={i}>
                  <div className="editorial-heading text-3xl md:text-5xl text-[#C9A961] leading-none">{k.value}</div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-stone-500 mt-2">{k.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

export function StatusPill({ tone = "stone", label }) {
  const palette = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-300",
    blue: "bg-sky-50 text-sky-700 border-sky-300",
    amber: "bg-amber-50 text-amber-800 border-amber-300",
    rose: "bg-rose-50 text-rose-700 border-rose-300",
    stone: "bg-stone-50 text-stone-600 border-stone-300",
    gold: "bg-[#C9A961]/10 text-[#A88A4A] border-[#C9A961]/40",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold rounded-sm border ${palette[tone] || palette.stone}`}>
      {label}
    </span>
  );
}
